import type { FC } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { Navigate } from 'react-router';
import Box from '@mui/material/Box';
import { applyAccessibleAppFromSearch, useGlobalSettings } from 'providers';
import useCheckAuth from '../hooks/useCheckAuth';
import { hotjar } from 'react-hotjar';
import datadogActions from 'lib/datadog';

const AuthSpinner = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

const AuthProvider: FC = () => {
  const isHotjar = hotjar.initialized();
  const location = useLocation();
  const { setSelectedApp } = useGlobalSettings();
  const {
    isAuthenticated,
    isAuthReady,
    isSuccess,
    isSuperuser,
    apps,
    isUserTracking,
    username,
    email,
    role,
    isError,
    error,
  } = useCheckAuth();
  const [isGlobalAppReady, setIsGlobalAppReady] = useState(false);

  useEffect(() => {
    if (isUserTracking)
      hotjar.initialize({ id: 5273642, sv: 7 });
  }, [isUserTracking]);

  useEffect(() => {
    if (isAuthenticated && isHotjar && isUserTracking) {
      hotjar.identify(username ?? '', { email: email ?? 'Email not specified by API' });
    }
  }, [isAuthenticated, username, email, isHotjar, isUserTracking]);

  useEffect(() => {
    if (!isAuthenticated || !username) {
      return;
    }

    datadogActions.setDatadogUser({
      id: username,
      email,
      role,
    });
  }, [isAuthenticated, username, email, role]);

  useLayoutEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;

    const app = applyAccessibleAppFromSearch(location.search, {
      isSuperuser,
      apps,
    });
    if (app) {
      setSelectedApp(app);
    }
    setIsGlobalAppReady(true);
  }, [apps, isAuthenticated, isAuthReady, isSuperuser, location.search, setSelectedApp]);

  if (isError) throw Error((error as { message?: string })?.message ?? 'User error');

  if (!isAuthReady || (isAuthenticated && !isGlobalAppReady)) {
    return <AuthSpinner />;
  }

  if (isSuccess && !isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <Outlet />;
};

export default AuthProvider;
